import { Component, computed, effect, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Bind } from "../../../decorators/bind.decorator";
import { ModalComponent } from "../../modal/modal.component";
import { ModalHeaderComponent } from "../../modal/modal.header.component";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Workflow } from "../service/Workflow";
import { WorkflowService } from "../service/workflow.service";
import { WorkflowProcessEditorComponent } from "../workflow.process.editor.component";
import { WorkflowActionAutoMapService } from "../workflow.action.auto.map.service";
import { WorkflowEditorInitialExpand, WorkflowSteps } from "../workflow.process.types";
import { INGESTION_POINT_TYPE_ID } from "../../data-ingestion/data.ingestion.const";
import { DataIngestionService, IngestionFieldDefinition } from "../../data-ingestion/data.ingestion.service";
import { S25Util } from "../../../util/s25-util";
import { StageActionGroup, WorkflowAddFormActionsModalData } from "./workflow.add.form.actions.modal.data";

type WizardStep = "pick" | "review";

@TypeManagerDecorator("workflow-add-form-actions-modal")
@Component({
    selector: "workflow-add-form-actions-modal",
    standalone: true,
    imports: [
        FormsModule,
        ModalHeaderComponent,
        S25ModalModule,
        S25CheckboxModule,
        S25ButtonComponent,
        WorkflowProcessEditorComponent,
    ],
    template: `
        <s25-modal-header [data]="params()" />
        <div class="modal-body">
            @if (wizardStep() === "pick") {
                <p class="step-hint">
                    Each option is a stage action on this workflow. Select actions to configure for this form.
                </p>
                @if (stageActionGroups().length === 0) {
                    <p>No stage actions are available to add for this form.</p>
                } @else {
                    <div class="action-picker-list">
                        @for (group of stageActionGroups(); track group.groupKey) {
                            <label class="picker-row" [class.disabled]="group.alreadyHasThisForm">
                                <input
                                    class="c-input"
                                    type="checkbox"
                                    [disabled]="group.alreadyHasThisForm"
                                    [checked]="selectedGroupKeys().has(group.groupKey)"
                                    (change)="toggleGroup(group.groupKey, $any($event.target).checked)"
                                />
                                <span class="picker-label">
                                    <strong>{{ group.displayName }}</strong>
                                    <span class="picker-sub">Stage: {{ group.stageName }}</span>
                                    @if (group.existingFormCount > 1) {
                                        <span class="picker-meta">Used with {{ group.existingFormCount }} form(s)</span>
                                    }
                                    @if (group.alreadyHasThisForm) {
                                        <span class="picker-meta">Already configured for this form</span>
                                    }
                                </span>
                            </label>
                        }
                    </div>
                }
            } @else {
                <workflow-process-editor
                    [(workflowProcess)]="workflowProcess"
                    [workflowProcessId]="modalData().workflowProcessId"
                    [embedded]="true"
                    [sourceFormId]="modalData().ingestionPointId"
                    [sourceFormName]="modalData().ingestionPointName"
                    [initialExpand]="editorInitialExpand()"
                    (saved)="onWorkflowSaved()"
                    (cancelled)="params()?.closeModal?.()"
                />
            }
        </div>
        @if (wizardStep() === "pick") {
            <s25-ng-modal-footer [content]="pickFooter" [bordered]="true" />
            <ng-template #pickFooter>
                <div class="modal-footer-actions">
                    <button type="button" class="aw-button aw-button--outline" (click)="params()?.closeModal?.()">
                        Cancel
                    </button>
                    <s25-ng-button
                        [type]="'primary'"
                        buttonText="Continue"
                        [disabled]="!selectedGroupKeys().size"
                        [onClick]="addSelectedActions"
                    />
                </div>
            </ng-template>
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: `
        .modal-body {
            max-height: 85vh;
            overflow-y: auto;
        }

        workflow-process-editor {
            display: block;
            width: 100%;
        }
        .step-hint {
            color: #4b5563;
            font-size: 13px;
            margin-bottom: 12px;
        }
        .action-picker-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .picker-row {
            display: flex;
            gap: 10px;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            cursor: pointer;
        }
        .picker-row.disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
        .picker-label {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }
        .picker-sub,
        .picker-meta {
            font-size: 12px;
            color: #6b7280;
        }
        .modal-footer-actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            padding: 12px 16px;
        }
    `,
})
export class WorkflowAddFormActionsModalComponent extends ModalComponent<WorkflowAddFormActionsModalData> {
    readonly wizardStep = signal<WizardStep>("pick");
    readonly selectedGroupKeys = signal<Set<string>>(new Set());
    readonly workflowProcess = signal<Workflow.WorkflowProcess>({} as Workflow.WorkflowProcess);

    private workflowSeededFromParams = false;

    private readonly syncWorkflowFromParams = effect(() => {
        const params = this.params();
        if (!params?.workflowProcess || this.workflowSeededFromParams) {
            return;
        }
        this.workflowProcess.set(S25Util.deepCopy(params.workflowProcess));
        this.workflowSeededFromParams = true;
    });

    readonly modalData = computed(() => this.params()!);

    readonly stageActionGroups = computed(() => this.buildStageActionGroups(this.workflowProcess()));

    readonly editorInitialExpand = computed<WorkflowEditorInitialExpand | undefined>(() => {
        const added = this.lastAddedAction();
        if (!added) {
            return {
                step: WorkflowSteps.Actions,
                formId: this.modalData().ingestionPointId,
            };
        }
        return {
            step: WorkflowSteps.Actions,
            formId: this.modalData().ingestionPointId,
            actionId: added.workflowActionId ?? added.id,
        };
    });

    private readonly lastAddedAction = signal<Workflow.WorkflowAction | undefined>(undefined);

    private buildStageActionGroups(process: Workflow.WorkflowProcess): StageActionGroup[] {
        const ingestionPointId = this.modalData().ingestionPointId;
        const byKey = new Map<string, StageActionGroup>();

        for (const stage of process.stages ?? []) {
            const stageId = stage.workflowStageId ?? stage.id!;
            for (const action of stage.actions ?? []) {
                const groupKey = `${stageId}:${action.actionType}:${action.actionCode ?? action.httpUrl ?? "http"}`;
                const existing = byKey.get(groupKey);
                const usesForm =
                    action.actionItemTypeId === INGESTION_POINT_TYPE_ID && action.actionItemId === ingestionPointId;
                if (existing) {
                    existing.existingFormCount += 1;
                    if (usesForm) existing.alreadyHasThisForm = true;
                    continue;
                }
                byKey.set(groupKey, {
                    groupKey,
                    stageId,
                    stageName: stage.stageName,
                    actionType: action.actionType,
                    actionCode: action.actionCode,
                    displayName: this.getActionDisplayName(action),
                    templateAction: action,
                    existingFormCount: 1,
                    alreadyHasThisForm: usesForm,
                });
            }
        }

        return [...byKey.values()].sort((left, right) =>
            left.stageName.localeCompare(right.stageName, undefined, { sensitivity: "base" }),
        );
    }

    toggleGroup(groupKey: string, checked: boolean): void {
        const next = new Set(this.selectedGroupKeys());
        if (checked) next.add(groupKey);
        else next.delete(groupKey);
        this.selectedGroupKeys.set(next);
    }

    @Bind
    async addSelectedActions(): Promise<void> {
        const selectedKeys = this.selectedGroupKeys();
        if (!selectedKeys.size) return;

        const process = this.workflowProcess();
        let fieldDefinitions: IngestionFieldDefinition[] = [];
        try {
            fieldDefinitions = await this.resolveTargetFieldDefinitions();
        } catch (caughtError) {
            console.error(
                "Could not load form fields for action input mapping; actions will be added without auto-mapping.",
                caughtError,
            );
        }

        const addedActions: Workflow.WorkflowAction[] = [];
        let lastAction: Workflow.WorkflowAction | undefined;

        for (const group of this.stageActionGroups()) {
            if (!selectedKeys.has(group.groupKey)) continue;

            const stage = process.stages?.find((row) => (row.workflowStageId ?? row.id) === group.stageId);
            if (!stage) continue;

            const clone = structuredClone(group.templateAction);
            clone.workflowActionId = undefined;
            clone.id = this.nextNegativeId(process);
            clone.workflowStageId = stage.workflowStageId ?? stage.id;
            clone.workflowProcessId = process.workflowProcessId;
            clone.actionItemId = this.modalData().ingestionPointId;
            clone.actionItemTypeId = INGESTION_POINT_TYPE_ID;
            clone.sortOrder = (stage.actions?.length ?? 0) + 1;
            clone.inputs?.forEach((input) => {
                input.workflowActionInputId = undefined;
                input.id = this.nextNegativeId(process);
            });
            clone.dispatchMappings?.forEach((mapping) => {
                mapping.workflowActionId = undefined;
            });

            if (!stage.actions) stage.actions = [];
            stage.actions.push(clone);
            addedActions.push(clone);
            lastAction = clone;
        }

        if (!addedActions.length) return;

        for (const addedAction of addedActions) {
            try {
                await WorkflowActionAutoMapService.autoMapActionInputs(
                    addedAction,
                    fieldDefinitions,
                    process,
                    undefined,
                    {
                        mode: "automatic",
                    },
                );
            } catch (caughtError) {
                console.error(
                    "Action input mapping failed; configure field mappings manually in the editor.",
                    caughtError,
                );
            }
        }

        const updatedProcess = S25Util.deepCopy(process);
        this.workflowProcess.set(updatedProcess);
        this.lastAddedAction.set(lastAction);
        this.wizardStep.set("review");
        this.params.update((row) => ({
            ...row!,
            title: "Configure workflow actions",
            workflowProcess: updatedProcess,
        }));
    }

    onWorkflowSaved(): void {
        this.params()?.closeModal?.(true);
    }

    private async resolveTargetFieldDefinitions(): Promise<IngestionFieldDefinition[]> {
        const fromModal = this.modalData().fieldDefinitions;
        if (fromModal?.length) {
            return fromModal;
        }
        const ingestionPointId = this.modalData().ingestionPointId;
        const [fields, error] = await S25Util.Maybe(DataIngestionService.getFieldDefinitions(ingestionPointId));
        if (error) {
            console.error("Could not load form fields for action input mapping:", error);
            return [];
        }
        return fields ?? [];
    }

    private getActionDisplayName(action: Workflow.WorkflowAction): string {
        if (action.description?.trim()) return action.description.trim();
        if (action.actionCode) {
            const systemDisplayName = WorkflowService.getSystemActionDisplayName(action.actionCode);
            if (systemDisplayName) {
                return systemDisplayName;
            }
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (letter) => letter.toUpperCase());
        }
        return action.actionType;
    }

    private nextNegativeId(process: Workflow.WorkflowProcess): number {
        let minId = 0;
        for (const stage of process.stages ?? []) {
            for (const action of stage.actions ?? []) {
                if (action.id != null) minId = Math.min(minId, action.id);
            }
        }
        return minId - 1;
    }
}
